import { useMemo } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

// The full [REDACTED:type:basis] form is what the pipeline is meant to
// produce, but a model-written summary/quote can simplify it down to a
// bare [REDACTED] while paraphrasing — confirmed live, so match both.
// [illegible] is Vision's mark for writing it couldn't read — handwriting,
// a poor scan. It is shown as unreadable, never as blacked out.
// [unsure: word] is its best reading of a word it could mostly make out:
// shown as that word, marked as a guess.
const REDACTION_RE = /\[REDACTED(?::[^\]]+)?\]|\[illegible\]|\[unsure:[^\]]*\]/gi;
const isIllegible = (marker) => /^\[illegible\]$/i.test(marker);
const UNSURE_RE = /^\[unsure:\s*([^\]]*?)\s*\]$/i;

/** The guessed word inside an [unsure: word] marker, or null for any other marker. */
export function unsureWord(marker) {
  const m = UNSURE_RE.exec(marker || '');
  return m ? m[1] : null;
}

/**
 * How much a marker stands for: a whole page, a whole section or box, a run
 * of lines, or (null) a word or two inline. Written by the Vision and
 * extraction prompts as [REDACTED:page], [REDACTED:section],
 * [REDACTED:paragraph], [REDACTED:lines N].
 */
export function markerExtent(marker) {
  const inner = marker.replace(/^\[REDACTED:?/, '').replace(/\]$/, '').toLowerCase();
  if (/^(whole )?page\b/.test(inner)) return 'page';
  if (/^(section|paragraph)\b/.test(inner)) return 'section';
  const lines = inner.match(/^lines?\s*(\d+)/);
  if (lines) return { lines: Number(lines[1]) };
  return null;
}

/** "[REDACTED:explicit:s40]" → "Redacted · explicit · s40"; bare "[REDACTED]" → "Redacted. No reason given." */
export function describeMarker(marker) {
  const extent = markerExtent(marker);
  if (extent === 'page') return 'The whole page was blacked out.';
  if (extent === 'section') return 'A whole section was blacked out.';
  if (extent && extent.lines) return `${extent.lines} lines were blacked out.`;
  const inner = marker.replace(/^\[REDACTED:?/, '').replace(/\]$/, '');
  const bits = inner.split(':').filter(Boolean);
  return bits.length ? `Redacted · ${bits.join(' · ')}` : 'Redacted. No reason given.';
}

/** The words shown on a block-sized redaction, or null for an inline one. */
export function blockLabel(marker) {
  const extent = markerExtent(marker);
  if (extent === 'page') return 'Whole page blacked out';
  if (extent === 'section') return 'Section blacked out';
  if (extent && extent.lines) return `${extent.lines} lines blacked out`;
  return null;
}

/** Split text on [REDACTED:*] markers into [{kind:'text'|'redaction', body}]. */
export function parseRedactionParts(text) {
  if (!text) return [];
  const out = [];
  let last = 0;
  let m;
  REDACTION_RE.lastIndex = 0;
  while ((m = REDACTION_RE.exec(text)) !== null) {
    if (m.index > last) out.push({ kind: 'text', body: text.slice(last, m.index) });
    const unsure = unsureWord(m[0]);
    if (unsure !== null) out.push({ kind: 'unsure', body: unsure });
    else out.push({ kind: isIllegible(m[0]) ? 'illegible' : 'redaction', body: m[0] });
    last = REDACTION_RE.lastIndex;
  }
  if (last < text.length) out.push({ kind: 'text', body: text.slice(last) });
  return out;
}

/**
 * Renders text with any [REDACTED:*] markers turned into the visual black
 * bar (with a tooltip explaining why), instead of showing the literal
 * bracket syntax. Use anywhere a record's full_text/representative_quote/
 * label is shown as plain text — a title, a card, a paragraph.
 */
export const RedactionAwareText = ({ text, as: As = 'span', className, testId, testIdPrefix }) => {
  const parts = useMemo(() => parseRedactionParts(text), [text]);
  if (!parts.length) return null;
  return (
    <TooltipProvider delayDuration={150}>
      <As className={className} data-testid={testId}>
        {parts.map((p, i) =>
          p.kind === 'text' ? (
            <span key={i}>{p.body}</span>
          ) : p.kind === 'unsure' ? (
            <span
              key={i}
              className="pd-mark-unsure"
              title="Hard to read — this is the best reading of the handwriting and may be wrong"
              aria-label={`best guess: ${p.body}`}
              data-testid={testIdPrefix ? `${testIdPrefix}-unsure-${i}` : undefined}
            >
              {p.body}
            </span>
          ) : p.kind === 'illegible' ? (
            <span
              key={i}
              className="pd-mark-illegible"
              title="This part couldn't be read — handwriting or a poor scan, not blacked out"
              aria-label="unreadable"
              data-testid={testIdPrefix ? `${testIdPrefix}-illegible-${i}` : undefined}
            >
              unreadable
            </span>
          ) : blockLabel(p.body) ? (
            // A whole page or section: a bar the width of the text, so how
            // much is gone is as visible as that something is.
            <span
              key={i}
              role="img"
              className="pd-mark-redacted block w-full my-3 py-4 text-center text-sm font-sans tracking-wide"
              aria-label={describeMarker(p.body)}
              data-testid={testIdPrefix ? `${testIdPrefix}-redaction-${i}` : undefined}
            >
              {blockLabel(p.body)}
            </span>
          ) : (
            <Tooltip key={i}>
              <TooltipTrigger asChild>
                <span
                  className="pd-mark-redacted cursor-help font-mono text-[15px]"
                  aria-label={describeMarker(p.body)}
                  data-testid={testIdPrefix ? `${testIdPrefix}-redaction-${i}` : undefined}
                >
                  {p.body}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs font-mono">
                {describeMarker(p.body)}
              </TooltipContent>
            </Tooltip>
          )
        )}
      </As>
    </TooltipProvider>
  );
};

export default RedactionAwareText;
